import Avatar from '@/components/elements/Avatar';
import Label from '@/components/elements/Label/Label';
import Alert from '@/components/elements/notifications/Alert/Alert';
import { RatingV2 } from '@/components/elements/redesign/RatingV2/RatingV2';
import Icon from '@/components/icons/Icon';
import { ModalContent, ModalDialog, ModalFooter, ModalHeader } from '@/components/modules/modals';
import { MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED } from '@/constants';
import { getBundleValidity } from '@/helpers/placeHelper';
import { _t } from '@/locale';
import { Place, PlaceBundle } from '@/types/state/shared';
import { ReactNode } from 'react';

const baseTranslationKey = 'components.modules.salon.BundleReadMoreModal';

type BundleReadMoreProps = {
  place: Place;
  bundle: PlaceBundle;
  onBuyClick: () => void;
  onClose: () => void;
};

const Dot = () => <span aria-hidden className="bg-fg_secondary h-[2px] w-[2px] shrink-0 rounded-full" />;

const MetaRow = ({ items, className }: { items: ReactNode[]; className: string }) => (
  <div className={`gap-xs text-fg_secondary flex items-center ${className}`}>
    {items.filter(Boolean).map((item, index) => (
      <span key={index} className="gap-xs flex items-center">
        {index > 0 && <Dot />}
        {item}
      </span>
    ))}
  </div>
);

const SECTION_CARD = 'bg-bg_neutral border-border_regular rounded-lg border';

const BundleReadMoreModal = ({ bundle, place, onBuyClick, onClose }: BundleReadMoreProps) => {
  const service = place.services
    .flatMap((group) => group.services)
    .find((placeService) => placeService.id === bundle.service.mpId);

  const employees = (bundle.employees ?? [])
    .map((id) => place.employees.find((employee) => employee.id === id))
    .filter((employee) => employee?.about);

  const employeeStats = place.reviews?.employeeStats;
  const hasDiscount = Boolean(bundle.finalPrice) && bundle.finalPrice !== bundle.price;
  const quantityLabel =
    bundle.service.quantity === 1
      ? _t('buyBundles.quantity_one', { count: bundle.service.quantity })
      : _t('buyBundles.quantity_other', { count: bundle.service.quantity });

  return (
    <ModalDialog appElement={document.getElementById('root')} isOpen onRequestClose={onClose}>
      <ModalContent size="lg" stickyHeader>
        <ModalHeader
          onClose={onClose}
          variant={'primary'}
          title={_t(`${baseTranslationKey}.title`)}
          position="sticky"
        />
        <div className="bg-surface_mp p-xl gap-xl flex flex-1 flex-col">
          <div className="gap-sm flex flex-col">
            <div className="gap-xs pb-xs flex items-center">
              <div className="gap-xs flex min-w-0 flex-1 flex-col">
                <h2 className="text-h-m text-fg_primary font-bold">{bundle.name}</h2>
                <MetaRow className="text-m" items={[bundle.durationLabel, quantityLabel]} />
              </div>
              <div className="gap-xs flex flex-col items-end whitespace-nowrap text-right">
                {hasDiscount ? (
                  <>
                    <span className="text-l text-fg_highlight font-bold">{bundle.finalPriceLabel}</span>
                    <s className="text-s text-fg_secondary">{bundle.priceLabel}</s>
                  </>
                ) : (
                  <span className="text-l text-fg_primary font-bold">{bundle.priceLabel}</span>
                )}
              </div>
            </div>
            <div className="border-border_regular border-t" />
            {bundle.description && <p className="text-m text-fg_secondary whitespace-pre-wrap">{bundle.description}</p>}
            {bundle.validMonths && (
              <div className="flex">
                <Label size="lg" label={getBundleValidity(bundle.validMonths)} />
              </div>
            )}
          </div>

          <section className="gap-md flex flex-col">
            <h3 className="text-h-s text-fg_primary font-bold">{_t(`${baseTranslationKey}.bookableServices`)}</h3>
            <div className={`${SECTION_CARD} gap-xs px-md py-sm flex flex-col`}>
              <span className="text-l text-fg_primary">{service?.name ?? bundle.service.name}</span>
              {service && <MetaRow className="text-s" items={[service.durationLabel, service.priceLabel]} />}
            </div>
          </section>

          {employees.length > 0 && (
            <section className="gap-md flex flex-col">
              <h3 className="text-h-s text-fg_primary font-bold">{_t(`${baseTranslationKey}.bookableWith`)}</h3>
              <ul className="gap-sm flex flex-col">
                {employees.map((employee) => {
                  const stats = employeeStats?.[employee.id];
                  return (
                    <li key={employee.id} className={`${SECTION_CARD} gap-sm py-sm pl-sm pr-xs flex items-center`}>
                      <Avatar
                        src={employee.about.settings?.showPhoto === false ? null : employee.about.avatar}
                        size="md"
                      />
                      <div className="gap-2xs flex min-w-0 flex-1 flex-col">
                        <span className="text-l text-fg_primary truncate">{employee.about.name}</span>
                        <div className="gap-xs flex items-center">
                          {stats?.score && stats.count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED ? (
                            <RatingV2
                              averageRating={stats.score}
                              size={'sm'}
                              withAverageScore
                              withMultipleStars={false}
                            />
                          ) : null}
                          {employee.about.jobTitle && (
                            <span className="text-s text-fg_secondary truncate">{employee.about.jobTitle}</span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <Alert
            variant="information"
            verticalAlign="top"
            leftSlot={<Icon variant="info-circle" />}
            body={_t(`${baseTranslationKey}.info`)}
          />
        </div>
        <ModalFooter
          onCloseClick={onClose}
          onCtaClick={onBuyClick}
          ctaLabel={_t(`${baseTranslationKey}.buy`)}
          position="sticky"
        />
      </ModalContent>
    </ModalDialog>
  );
};

export default BundleReadMoreModal;
